import React, { createContext, useContext, useLayoutEffect, useState } from 'react';

// Theme is applied as `data-theme` on <html> so CSS custom properties in
// src/styles/global.scss can be overridden per mode. Persisted to localStorage;
// first-load default falls back to the OS preference.
//
// public/index.html runs the same resolution inline before the bundle loads so a
// dark-mode user never sees a white first paint. getInitialTheme below must stay
// in sync with that snippet — if the two disagree the page flips theme on mount.

const ThemeContext = createContext(null);
const STORAGE_KEY = 'grap-theme';

function getInitialTheme() {
  if (typeof window === 'undefined') return 'light';
  // 저장소 접근이 차단된 환경(서드파티 쿠키 차단 등)에서는 getItem 자체가
  // SecurityError 를 던집니다. 이 함수는 useState 초기화에서 불리므로
  // ErrorBoundary 보다 위에서 터져 화면 전체가 비어 버립니다. index.html 의
  // 부트스트랩과 동일하게, 실패하면 OS 설정으로 넘어가도록 감쌉니다.
  let stored = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch (_) {
    // 아래 matchMedia 분기로 이어집니다.
  }
  if (stored === 'light' || stored === 'dark') return stored;
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  // useEffect 로 두면 첫 페인트가 끝난 뒤에 data-theme 이 붙습니다. 첫 렌더에서
  // antd 는 이미 다크 토큰으로 그려지는데 CSS 변수는 아직 라이트라, 다크로 접속한
  // 사용자에게 흰 화면이 한 프레임 스쳐 지나갑니다. useLayoutEffect 는 페인트
  // 직전에 실행되므로 그 깜빡임이 생기지 않습니다.
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {
      // localStorage may be unavailable (private mode); the data-theme attr is still applied.
    }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
